import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import ErrorBanner from '@avtechsoftware/react-component-library/src/components/common/ErrorBanner';
import moment from 'moment';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import axios from 'axios';
import { faFileLines, faCreditCard } from '@fortawesome/pro-regular-svg-icons';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import FormAlertBox
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormAlertBox/FormAlertBox';
import envConfig from '../../../envConfig';
import AddressBlock from '../../orders/AddressBlock/AddressBlock';
import LineItemTable from '../../orders/LineItemTable/LineItemTable';
import EditAddressModal from './EditAddressModal/EditAddressModal';
import ClassUtility from '../../../utility/ClassUtility';
import AddressType from './AddressType';
import OrderPaymentForm from '../../orders/OrderPaymentForm/OrderPaymentForm';
import FileUploadInput from '../../orders/FileUploadInput';
import CrmQuote from '../../../classes/CrmQuote';
import ShareDropdown from '../../orders_quotes_shared/ShareDropdown/ShareDropdown';
import LinkModal from '../../common/LinkModal/LinkModal';
import GrantAccessModal from '../../orders_quotes_shared/GrantAccessModal/GrantAccessModal';
import {
  generateResourceURL,
} from '../../../utility/orderAndQuoteUtil';
import { connectModalContainerContextToComponent } from '../../../contexts/ModalContext/ModalContext';
import VerifyAddressModal from './VerifyAddressModal/VerifyAddressModal';

function QuoteDetails({
  quoteDetails,
  isPublicView,
  supportsRoles,
  samlEnabled,
  userHasGrantAccessPrivileges,
  email,
  countryList,
}) {
  const [quote, setQuote] = useState(quoteDetails);
  const history = useHistory();
  const [formErrors, setFormErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expirationYear, setExpirationYear] = useState(moment().year());
  const [expirationMonth, setExpirationMonth] = useState('01');
  const [ccv, setCcv] = useState('');
  const [poFile, setPoFile] = useState(null);
  const [usingCreditCard, setUsingCreditCard] = useState(true);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [showGrantAccessModal, setShowGrantAccessModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [inputEmail, setInputEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addressType, setAddressType] = useState(AddressType.SHIPPING);
  const [addressToEdit, setAddressToEdit] = useState(quote.shippingAddress);
  const [isShippingRateCalculated, setIsShippingRateCalculated] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingFormErrors, setShippingFormErrors] = useState([]);
  const [shippingRatesPending, setShippingRatesPending] = useState(false);
  const [calculatingTotals, setCalculatingTotals] = useState(false);
  const [showVerifyAddressModal, setShowVerifyAddressModal] = useState(
    quote.hasShipment && quote.statusType && quote.statusType !== 'Closed',
  );

  const poFileInfo = 'Please upload your PO before placing the order. '
    + 'Accepted file formats include PDF, DOC and DOCX, TXT, PNG, JPG, or CSV.';
  /* istanbul ignore next */
  const onCloseError = () => {
    setError(false);
  };

  const backToList = () => {
    history.push('/s/orders/quotes/list');
  };

  const handleOpenEditAddressModal = (type) => {
    if (type === AddressType.SHIPPING && !quote.hasShipment) return;
    const address = type === AddressType.SHIPPING
      ? quote.shippingAddress
      : quote.billingAddress;
    setAddressToEdit(address);
    setAddressType(type);
    setShowEditAddressModal(true);
  };

  // Unreachable since modal component is rendered outside this one
  /* istanbul ignore next */
  const handleRefreshShippingOptions = (address) => {
    setShippingRatesPending(true);
    setShippingFormErrors([]);
    setIsShippingRateCalculated(false);
    const formData = new FormData();

    formData.append('contactName', address.contactName);
    formData.append('companyName', address.companyName);
    formData.append('line1', address.line1);
    formData.append('city', address.city);
    formData.append('state', address.state);
    formData.append('country', address.country.code ? address.country.code : '');
    formData.append('zip', address.zipCode);
    formData.append('quoteId', quote.id);
    axios.post(`${envConfig.raaUrl}/quote/shipping-rates`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((response) => {
        if (response.data && response.data.success === false) {
          setShippingFormErrors([...response.data.data]);
        } else {
          setShippingOptions([...response.data.data]);
          setIsShippingRateCalculated(true);
        }
      })
      .catch(() => {
        setShippingFormErrors([{ message: 'Unexpected error getting shipping rates' }]);
      })
      .finally(() => {
        setShippingRatesPending(false);
      });
  };

  // Unreachable since modal component is rendered outside this one
  /* istanbul ignore next */
  const handleUpdateShipping = (newShippingOption, newAddress) => {
    setIsShippingRateCalculated(false);
    setShippingRatesPending(false);
    setShippingFormErrors([]);
    setShowEditAddressModal(false);
    setShippingOptions([]);
    const formData = new FormData();
    const { price, serviceName, carrierName } = newShippingOption;
    const updatedQuote = ClassUtility.cloneObject(quote);
    updatedQuote.shippingAddress = newAddress;
    updatedQuote.shippingMethod = serviceName;
    updatedQuote.shippingCarrier = carrierName;
    updatedQuote.shippingCost = parseFloat(price);

    setCalculatingTotals(true);
    formData.append('shippingMethod', serviceName);
    formData.append('shippingAmount', price);
    formData.append('quoteId', updatedQuote.id);
    formData.append('line1', updatedQuote.shippingAddress.line1);
    formData.append('line2', updatedQuote.shippingAddress.line2);
    formData.append('city', updatedQuote.shippingAddress.city);
    formData.append('region', updatedQuote.shippingAddress.state);
    formData.append('country', updatedQuote.shippingAddress.country.code);
    formData.append('postalCode', updatedQuote.shippingAddress.zipCode);

    axios.post(`${envConfig.raaUrl}/quote/tax`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((response) => {
        if (response.data && response.data.success === false) {
          setShippingFormErrors([...response.data.data]);
        } else {
          updatedQuote.tax = response.data.data.tax;
          setQuote(updatedQuote);
        }
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setCalculatingTotals(false);
      });
  };

  // Unreachable since modal component is rendered outside this one
  /* istanbul ignore next */
  const handleUpdateAddress = (type, editedAddress, selectedShipping) => {
    const updatedQuote = ClassUtility.cloneObject(quote);
    if (addressType === AddressType.SHIPPING) {
      handleUpdateShipping(selectedShipping, editedAddress);
    } else if (addressType === AddressType.BILLING) {
      updatedQuote.billingAddress = editedAddress;
      setQuote(updatedQuote);
    }
  };

  const handleSubmitPayment = () => {
    setSubmitting(true);
    const formData = new FormData();
    formData.append('quoteId', quote.id);
    formData.append('email', isPublicView ? inputEmail : email);
    formData.append('contactPhone', phone);
    formData.append('purchaseOrderNumber', purchaseOrderNumber);
    formData.append('customerComment', '');
    formData.append('cardNumber', cardNumber);
    formData.append('shippingAddressCompanyName', quote.shippingAddress.companyName);
    formData.append('shippingAddressContactName', quote.shippingAddress.contactName);
    formData.append('shippingAddressAddressLine1', quote.shippingAddress.line1);
    formData.append('shippingAddressAddressLine2', quote.shippingAddress.line2);
    formData.append('shippingAddressCity', quote.shippingAddress.city);
    formData.append('shippingAddressState', quote.shippingAddress.state);
    formData.append('shippingAddressZip', quote.shippingAddress.zipCode);
    formData.append('shippingAddressCountryId', quote.shippingAddress.country.id);
    formData.append('billingAddressCompanyName', quote.billingAddress.companyName);
    formData.append('billingAddressContactName', quote.billingAddress.contactName);
    formData.append('billingAddressAddressLine1', quote.billingAddress.line1);
    formData.append('billingAddressAddressLine2', quote.billingAddress.line2);
    formData.append('billingAddressCity', quote.billingAddress.city);
    formData.append('billingAddressState', quote.billingAddress.state);
    formData.append('billingAddressZip', quote.billingAddress.zipCode);
    formData.append('billingAddressCountryId', quote.billingAddress.country.id);
    formData.append('billing', quote.billingAddress);
    formData.append('shipping', quote.shippingAddress);
    formData.append('cardExpirationMonth', expirationMonth.toString());
    formData.append('cardExpirationYear', (expirationYear % 100).toString());
    formData.append('cardCvv', ccv);
    formData.append('paymentMethod', usingCreditCard ? 'Online - CC' : 'Purchase Order');
    formData.append('purchaseOrderFile', usingCreditCard ? null : poFile);
    formData.append('needsShipping', quote.needsShipping);
    formData.append('shippingCarrier', quote.shippingCarrier);
    formData.append('shippingMethod', quote.shippingMethod);
    formData.append('shippingPrice', quote.shippingCost);
    formData.append('tax', quote.tax);

    axios.post(`${envConfig.raaUrl}/quote/order`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((response) => {
        if (response.data && response.data.success === false) {
          setFormErrors([...response.data.data]);
        } else {
          const { orderId } = response.data.data;
          if (isPublicView) {
            history.push(`/p/order/ORD-${generateResourceURL(orderId, quote.crmOrgId)}`);
          } else {
            history.push(`/s/order/details/ORD-${generateResourceURL(orderId, quote.crmOrgId)}`);
          }
        }
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  /* istanbul ignore next */
  return (
    <div className="container-fluid mt-3">
      <div className="col-12">
        {error && (
        <div className="row">
          <div className="col">
            <ErrorBanner onCloseError={onCloseError}>
              Unexpected Error retrieving quote.
            </ErrorBanner>
          </div>
        </div>
        )}
        <div className="row">
          <div className="col">
            <h2>
              Quote
              {' '}
              {quote.quoteNumber}
            </h2>
            {quote.createdAt && (
            <span className="pl-1">
              Quoted on
              {' '}
              {moment.unix(quote.createdAt).format('MMM DD, YYYY')}
            </span>
            )}
          </div>
          {
            !isPublicView && (
            <div className="ml-auto col-auto">
              <button
                type="button"
                className="btn btn-secondary ml-auto"
                onClick={backToList}
              >
                Back to List
              </button>
            </div>
            )
          }
        </div>
        <hr className="mb-4" />
        <div className="row">
          <div className="col clearfix">
            <div className="d-flex flex-nowrap float-left">
              <div className="mr-2">
                {quote.statusType && quote.statusType !== 'Closed'
                  ? (
                    <FontAwesomeIcon
                      size="2x"
                      icon={faCircle}
                      className="text-primary"
                      style={{ verticalAlign: 'middle', marginTop: -3 }}
                    />
                  )
                  : (
                    <FontAwesomeIcon
                      size="2x"
                      icon={faTimesCircle}
                      className="text-muted"
                      style={{ verticalAlign: 'middle', marginTop: -3 }}
                    />
                  )}
              </div>
              {' '}
              <p className="mb-0">
                {quote.statusType && quote.statusType !== 'Closed'
                  ? `Valid through ${moment.unix(quote.expiresAt).format('MMM DD, YYYY')}`
                  : 'Closed'}
              </p>
            </div>
          </div>
          <div className="col ml-auto text-right">
            <span>
              {' '}
              <b>Product Specialist:</b>
              {' '}
              {quote.productSpecialist.name}
            </span>
            <br />
            <div className="text-right">
              {quote.productSpecialist.phone != null ? quote.productSpecialist.phone : ''}
              {' '}
              {quote.productSpecialist.phoneExtension != null ? `x${quote.productSpecialist.phoneExtension}` : ''}
            </div>
          </div>
        </div>

        {!isPublicView && (
        <div className="row mt-2 text-right">
          <div className="col ml-auto">
            <ShareDropdown
              isQuote
              onGetLink={
                      /* istanbul ignore next */
                      () => setShowLinkModal(true)
                    }
              onGrantAccountAccess={
                      /* istanbul ignore next */
                      () => setShowGrantAccessModal(true)
                    }
              downloadPdfHref={`${envConfig.raaUrl}/quote/requested-pdf/${quote.id}`}
              showGrantAccessOption={userHasGrantAccessPrivileges}
            />
          </div>
        </div>
        )}

        <div className="row mt-3">
          {quote.hasShipment && (
          <div className="col">
            <AddressBlock
              canEdit={quote.hasShipment && quote.statusType && quote.statusType !== 'Closed'}
              handleOpenModal={handleOpenEditAddressModal}
              addressMissing={quote.shippingAddress.line1.length <= 0}
              zipCode={quote.shippingAddress.zipCode}
              country={quote.shippingAddress.country ? quote.shippingAddress.country.name : ''}
              city={quote.shippingAddress.city}
              contactName={quote.shippingAddress.contactName}
              companyName={quote.shippingAddress.companyName}
              addrLine2={quote.shippingAddress.line2}
              addrLine1={quote.shippingAddress.line1}
              state={quote.shippingAddress.state}
              title="Shipping Address"
              type={AddressType.SHIPPING}
            />
          </div>
          )}
          <div className="col">
            <AddressBlock
              canEdit={quote.statusType && quote.statusType !== 'Closed'}
              handleOpenModal={handleOpenEditAddressModal}
              addressMissing={quote.billingAddress.line1.length <= 0}
              zipCode={quote.billingAddress.zipCode}
              country={quote.billingAddress.country ? quote.billingAddress.country.name : ''}
              city={quote.billingAddress.city}
              contactName={quote.billingAddress.contactName}
              addrLine2={quote.billingAddress.line2}
              addrLine1={quote.billingAddress.line1}
              state={quote.billingAddress.state}
              title="Billing Address"
              type={AddressType.BILLING}
            />
          </div>
        </div>

        <LineItemTable
          shippingCost={quote.shippingCostDisplay}
          grandTotal={quote.grandTotalDisplay}
          tax={quote.taxDisplay}
          lineItems={quote.quoteLineItemList}
          isQuote
          shippingDetailsString={quote.shippingDetailsString}
          currencyType={quote.currency}
          calculatingTotals={calculatingTotals}
        />
        <hr />

        {
                quote.statusType && quote.statusType !== 'Closed'
                  ? (
                    <FormErrorHandler
                      isFormDisabled={submitting}
                      errorList={formErrors}
                      fieldList={['cardCvv', 'cardNumber', 'cardExpirationYear', 'email', 'contactPhone', 'purchaseOrderNumber']}
                    >
                      {/* eslint-disable jsx-a11y/label-has-associated-control */}
                      <div className="pb-5 pt-3">
                        <FormAlertBox />
                        <div className="row mb-3">
                          <label className="col-form-label col-sm-3 text-sm-right text-nowrap"><b>Payment Method</b></label>
                          <div className="col-sm-9 pt-2">
                            <div className="row">
                              <div className="col">
                                <input
                                  type="radio"
                                  value="email@example.com"
                                  checked={usingCreditCard}
                                  onChange={() => setUsingCreditCard(true)}
                                />
                                <label className="ml-1 form-check-label">
                                  Credit Card -
                                  {' '}
                                  <FontAwesomeIcon icon={faCreditCard} />
                                </label>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col">
                                <input
                                  type="radio"
                                  value="email@example.com"
                                  checked={!usingCreditCard}
                                  onChange={() => {
                                    setUsingCreditCard(false);
                                    setCardNumber('');
                                    setExpirationMonth('01');
                                    setExpirationYear(moment().year());
                                    setCcv('');
                                  }}
                                />
                                <label className="ml-1 form-check-label">
                                  Pay on Terms -
                                  {' '}
                                  <FontAwesomeIcon icon={faFileLines} />
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mb-3">
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label className="col-form-label col-sm-3 text-sm-right"><b>Phone</b></label>
                          <div className="col-sm-9 col-xl-4 col-lg-6">
                            <AltErrorWrapper fieldId="contactPhone">
                              <input
                                type="text"
                                className="form-control w-75"
                                value={phone}
                                onChange={(e) => { setPhone(e.target.value); }}
                              />
                            </AltErrorWrapper>
                          </div>
                        </div>
                        {
                          isPublicView && (
                          <div className="row mb-3">
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label className="col-form-label col-sm-3 text-sm-right"><b>Email</b></label>
                            <div className="col-sm-9 col-xl-4 col-lg-6">
                              <AltErrorWrapper fieldId="email">
                                <input
                                  type="text"
                                  className="form-control w-75"
                                  value={inputEmail}
                                  onChange={(e) => { setInputEmail(e.target.value); }}
                                />
                              </AltErrorWrapper>
                            </div>
                          </div>
                          )
                        }
                        {
                        usingCreditCard
                          ? (
                            <OrderPaymentForm
                              expirationYear={expirationYear}
                              isFormDisabled={submitting}
                              setExpirationYear={setExpirationYear}
                              setCcv={setCcv}
                              setCardNumber={setCardNumber}
                              ccv={ccv}
                              formErrors={formErrors}
                              expirationMonth={expirationMonth}
                              setExpirationMonth={setExpirationMonth}
                              cardNumber={cardNumber}
                              showErrorBanner={false}
                            />
                          )
                          : (
                            <AltErrorWrapper fieldId="purchaseOrderFile">
                              <FileUploadInput
                                file={poFile}
                                setFile={setPoFile}
                                infoMsg={poFileInfo}
                              />
                            </AltErrorWrapper>
                          )
                      }
                        <div className="form-group row">
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label className="col-form-label col-sm-3 text-sm-right text-sm-nowrap">
                            <b>Purchase Order Number</b>
                          </label>
                          <div className="col-sm-8">
                            <AltErrorWrapper fieldId="purchaseOrderNumber">
                              <input
                                type="text"
                                placeholder={usingCreditCard ? 'Optional' : ''}
                                className="form-control always-inline"
                                onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                              />
                            </AltErrorWrapper>

                          </div>
                        </div>
                        <div className="form-group row">
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label className="col-form-label col-sm-3 text-sm-right text-sm-nowrap">
                            <b>Order Notes & Instructions</b>
                          </label>
                          <div className="col-sm-8">
                            <textarea
                              rows={3}
                              placeholder="Optional"
                              className="form-control always-inline"
                            />
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-sm-3" />
                          <div className="col-sm-9">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleSubmitPayment}
                              disabled={!usingCreditCard && !poFile}
                            >
                              {submitting ? (
                                <>
                                  <ProgressSpinner progressText="Processing..." useSmallSpinner />
                                </>
                              ) : 'Submit Payment'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </FormErrorHandler>
                  )
                  : null
            }
      </div>
      {showVerifyAddressModal && quote.hasShipment && (
        <VerifyAddressModal
          onClose={() => setShowVerifyAddressModal(false)}
        />
      )}
      {showLinkModal && (
        <LinkModal
          header="Get Link To This Quote"
          url={`${envConfig.raaUrl}/p/quote/QUO-${generateResourceURL(quote.id, quote.crmOrgId)}`}
          onClose={() => setShowLinkModal(false)}
        />
      )}
      {showGrantAccessModal && (
        <GrantAccessModal
          hasAdvancedRoles={supportsRoles}
          isSamlSubscription={samlEnabled}
          onClose={() => setShowGrantAccessModal(false)}
          isQuote
          resourceId={quote.id}
        />
      )}
      {showEditAddressModal && (
      <EditAddressModal
        closeModal={() => {
          setIsShippingRateCalculated(false);
          setShippingRatesPending(false);
          setShippingFormErrors([]);
          setShowEditAddressModal(false);
          setShippingOptions([]);
        }}
        address={addressToEdit}
        submitAddress={handleUpdateAddress}
        addressType={addressType}
        countries={countryList}
        isShippingRateCalculated={isShippingRateCalculated}
        shippingOptions={shippingOptions}
        handleRefreshShippingOptions={handleRefreshShippingOptions}
        shippingRatesPending={shippingRatesPending}
        shippingFormErrors={shippingFormErrors}
      />
      )}
    </div>
  );
}
QuoteDetails.defaultProps = {
  countryList: [],
};
QuoteDetails.propTypes = {
  countryList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number || null,
    crmId: PropTypes.number,
    code: PropTypes.number || null,
    name: PropTypes.string,
    abbreviation: PropTypes.string || null,
  })),
  quoteDetails: PropTypes.shape(CrmQuote).isRequired,
  userHasGrantAccessPrivileges: PropTypes.bool.isRequired,
  supportsRoles: PropTypes.bool.isRequired,
  samlEnabled: PropTypes.bool.isRequired,
  isPublicView: PropTypes.bool,
  email: PropTypes.string,
};

QuoteDetails.defaultProps = {
  isPublicView: false,
  email: '',
};

export { QuoteDetails };
export default connectModalContainerContextToComponent(QuoteDetails);
